import { db, withSyncWrite } from '../db/indexeddb'
import { getCloudTasksByIds, getCloudTagIds } from '../supabase/queries'
import { resolveLocalProjectId, resolveLocalSectionId } from './sync-resolve'

/**
 * Repair & Cleanup Helpers
 * Post-sync maintenance operations.
 */

/**
 * Hard-delete soft-deleted tasks/projects/sections that have already been synced to cloud.
 * Only removes records where deletedAt is set AND the deletion was pushed (no
 * pending 'delete' action in the sync queue for that record).
 */
export async function purgeSyncedDeletions(): Promise<void> {
  try {
    const pendingDeletes = await db.syncQueue
      .filter(item => item.action === 'delete' && !item.synced)
      .toArray()
    // Build pending-ID sets in a single pass
    const pendingIds: Record<string, Set<string>> = { tasks: new Set(), projects: new Set(), sections: new Set(), tags: new Set(), goals: new Set() }
    for (const item of pendingDeletes) {
      const id = item.data?.localId as string
      if (id && item.table in pendingIds) pendingIds[item.table].add(id)
    }

    // Hard-delete synced soft-deleted records using bulkDelete (single transaction per table)
    const purgeTable = async (table: typeof db.tasks | typeof db.projects | typeof db.sections | typeof db.tags | typeof db.goals, pending: Set<string>) => {
      const deleted = await table.filter((r: any) => r.deletedAt !== null).toArray()
      const idsToDelete = deleted.filter(r => !pending.has(r.id)).map(r => r.id)
      if (idsToDelete.length) await table.bulkDelete(idsToDelete)
    }

    await purgeTable(db.tasks, pendingIds.tasks)
    await purgeTable(db.projects, pendingIds.projects)
    await purgeTable(db.sections, pendingIds.sections)
    await purgeTable(db.tags, pendingIds.tags)
    await purgeTable(db.goals, pendingIds.goals)
  } catch (error) {
    console.warn('[Sync] purgeSyncedDeletions failed:', error)
  }
}

/**
 * Repair tasks with null projectId/sectionId that should have associations.
 * Finds local tasks where cloudId is set but projectId or sectionId is null,
 * fetches their cloud data to get project_id/section_id, and resolves locally.
 * This fixes tasks that were pulled before their project/section existed locally.
 */
export async function repairOrphanedTaskAssociations(userId: string): Promise<{ repaired: number }> {
  let repaired = 0
  try {
    const allTasks = await db.tasks.filter(t => !!t.cloudId && !t.deletedAt).toArray()
    const orphaned = allTasks.filter(t => t.projectId === null || t.projectId === undefined)

    if (orphaned.length === 0) return { repaired }

    const orphanedCloudIds = orphaned.flatMap(t => t.cloudId ? [t.cloudId] : [])
    if (orphanedCloudIds.length === 0) return { repaired }

    const cloudTasks = await getCloudTasksByIds(userId, orphanedCloudIds)
    const cloudTaskMap = new Map(cloudTasks.map(ct => [ct.id, ct]))

    for (const task of orphaned) {
      const cloudTask = cloudTaskMap.get(task.cloudId!)
      if (!cloudTask) continue

      const updates: Record<string, any> = {}

      if (!task.projectId && cloudTask.project_id) {
        const localProjectId = await resolveLocalProjectId(cloudTask.project_id)
        if (localProjectId) updates.projectId = localProjectId
      }

      if (!task.sectionId && cloudTask.section_id) {
        const localSectionId = await resolveLocalSectionId(cloudTask.section_id)
        if (localSectionId) updates.sectionId = localSectionId
      }

      if (Object.keys(updates).length > 0) {
        await withSyncWrite(() =>
          db.tasks.update(task.id, { ...updates, synced: true, syncedAt: new Date() })
        )
        repaired++
      }
    }

    if (repaired > 0) {
      console.log(`[Sync] Repaired ${repaired} orphaned task associations (projectId/sectionId)`)
    }
  } catch (error) {
    console.warn('[Sync] repairOrphanedTaskAssociations failed:', error)
  }
  return { repaired }
}

/**
 * Remove local tags whose cloudId no longer exists in Supabase (hard-deleted in cloud).
 * Also re-maps any task references from the orphaned tag to a surviving tag with the same name.
 */
export async function purgeOrphanedCloudTags(userId: string): Promise<number> {
  try {
    const localTags = await db.tags.filter(t => !!t.cloudId && !t.deletedAt).toArray()
    if (localTags.length === 0) return 0

    const cloudTagIds = new Set(await getCloudTagIds(userId))
    const orphaned = localTags.filter(t => !cloudTagIds.has(t.cloudId!))
    if (orphaned.length === 0) return 0

    const survivingTags = localTags.filter(t => cloudTagIds.has(t.cloudId!))

    for (const orphan of orphaned) {
      // Try to re-map task references to a surviving tag with the same name
      const replacement = survivingTags.find(t => t.name.toLowerCase() === orphan.name.toLowerCase())
      if (replacement) {
        const tasksWithOrphan = await db.tasks.filter(t => t.tags.includes(orphan.id)).toArray()
        for (const task of tasksWithOrphan) {
          const updatedTags = task.tags.map(id => id === orphan.id ? replacement.id : id)
          await withSyncWrite(() => db.tasks.update(task.id, { tags: updatedTags }))
        }
      } else {
        // No replacement — just remove the orphan ID from tasks
        const tasksWithOrphan = await db.tasks.filter(t => t.tags.includes(orphan.id)).toArray()
        for (const task of tasksWithOrphan) {
          const updatedTags = task.tags.filter(id => id !== orphan.id)
          await withSyncWrite(() => db.tasks.update(task.id, { tags: updatedTags }))
        }
      }

      await withSyncWrite(() => db.tags.delete(orphan.id))
    }

    if (orphaned.length > 0) {
      console.log(`[Sync] Purged ${orphaned.length} orphaned cloud tag(s)`)
    }
    return orphaned.length
  } catch (error) {
    console.warn('[Sync] purgeOrphanedCloudTags failed:', error)
    return 0
  }
}
